// Cobertura de la carta (RES-39): qué productos ya descuentan stock y cuáles todavía no. Sin
// dependencias de React para poder testearlo puro.

/**
 * Normaliza la respuesta de `GET /inventory/coverage` (listas siempre presentes).
 * @param {Object|null|undefined} data - Respuesta del backend
 * @returns {Object} Cobertura con `pending`, `covered`, `combos`, `unavailable` como arrays
 */
export function normalizeCoverage(data) {
  const source = data && typeof data === 'object' ? data : {};
  const list = (value) => (Array.isArray(value) ? value : []);
  const pending = list(source.pending);
  const covered = list(source.covered);
  return recomputeCoverage({
    restaurantId: source.restaurantId || null,
    periodDays: source.periodDays ?? 30,
    salesAvailable: source.salesAvailable !== false,
    pending,
    covered,
    combos: list(source.combos),
    unavailable: list(source.unavailable),
  });
}

/**
 * Recalcula los totales a partir de las listas (mismo criterio que el backend: combos y no
 * disponibles no cuentan en el total).
 * @param {Object} coverage - Cobertura con `pending` y `covered`
 * @returns {Object} Cobertura con `totalProducts`, `coveredCount`, `pendingCount`, `coveragePercent`
 */
export function recomputeCoverage(coverage) {
  const coveredCount = coverage.covered.length;
  const pendingCount = coverage.pending.length;
  const totalProducts = coveredCount + pendingCount;
  return {
    ...coverage,
    totalProducts,
    coveredCount,
    pendingCount,
    coveragePercent: totalProducts ? Math.round((coveredCount / totalProducts) * 100) : 0,
  };
}

/**
 * Pasa un producto de pendiente a cubierto sin volver a pedir la cobertura (el panel se
 * actualiza apenas se vincula).
 * @param {Object} coverage - Cobertura normalizada
 * @param {string} itemId - Producto de la carta
 * @param {'LINK'|'RECIPE'} [mode='LINK'] - Cómo descuenta ahora
 * @returns {Object} Cobertura nueva (la original no se toca)
 */
export function markProductCovered(coverage, itemId, mode = 'LINK') {
  const product = coverage.pending.find((p) => p.itemId === itemId);
  if (!product) return coverage;
  const { deferred, ...rest } = product;
  const covered = [...coverage.covered, { ...rest, mode }].sort(
    (a, b) => (b.unitsSold || 0) - (a.unitsSold || 0)
  );
  return recomputeCoverage({
    ...coverage,
    pending: coverage.pending.filter((p) => p.itemId !== itemId),
    covered,
  });
}

/**
 * Insumos del catálogo que se pueden elegir para vincular un producto de reventa: activos y
 * con control de stock, los de reventa primero.
 * @param {Object[]} items - Catálogo (`itemId`, `name`, `kind`, `trackStock`, `isActive`)
 * @returns {Object[]} Opciones ordenadas
 */
export function linkableCatalogItems(items = []) {
  return items
    .filter((item) => item && item.itemId && item.isActive !== false && item.trackStock !== false)
    .sort((a, b) => {
      const resaleA = a.kind === 'RESALE' ? 0 : 1;
      const resaleB = b.kind === 'RESALE' ? 0 : 1;
      return resaleA - resaleB || String(a.name || '').localeCompare(String(b.name || ''), 'es');
    });
}
